import {Alert, Box, Button, CircularProgress, FormHelperText, Grid, Stack, Typography} from '@mui/material';
import {useState} from 'react';
import dayjs, {Dayjs} from 'dayjs';
import {Controller} from 'react-hook-form';
import {DateCalendar} from '@mui/x-date-pickers';
import {useRegisterApplication} from "./useRegisterApplication";
import {FileInputForm, IinInput, TextFieldForm} from '@/global/components/FormInputs';
import {OrganizationPublicAutocomplete, PassCategorySelect} from '@/modules/exam-applications/shared';
import {LoadingButton} from "@mui/lab";
import type {StudentDto} from "@/global/api/gen/ts/StudentDto";

const min = dayjs().add(1, 'day');

interface Props {
    studentInfo: StudentDto,
    returnFirstStep: () => void,
}

export function ApplicationRegisterForm({studentInfo, returnFirstStep}: Props) {
    const {
        control,
        onFormSubmit,
        isPending,
        clearErrors,
        organizationId,
        slots,
        availableDays,
        isScheduleDataLoading,
        isScheduleMonthLoading
    } = useRegisterApplication({studentInfo, afterSubmit: returnFirstStep});
    const [minDate] = useState(min);

    return (
        <Stack
            width={{lg: '60%', xs: '100%'}}
            flexDirection={'column'}
            gap={2}
            sx={{
                margin: '0 auto',
                p: 4,
                borderRadius: '26px',
                background: '#F5F5F5',
                gap: '16px'
            }}
        >
            <Stack
                component="form"
                gap={2}
                onSubmit={onFormSubmit}
            >

                <IinInput
                    control={control}
                    name="iin"
                    disabled={true}
                    resetErrors={clearErrors}
                    label="ИИН"
                    placeholder="ИИН"
                />

                <TextFieldForm
                    control={control}
                    name="lastName"
                    label="Фамилия"
                    placeholder="Фамилия"
                />

                <TextFieldForm
                    control={control}
                    name="firstName"
                    label="Имя"
                    placeholder="Имя"
                />

                <TextFieldForm
                    control={control}
                    name="patronymic"
                    label="Отчество"
                    placeholder="Отчество"
                />

                <Controller
                    control={control}
                    name="category"
                    render={({field, fieldState}) => (
                        <>
                            <PassCategorySelect
                                value={field.value}
                                onChange={(e) => {
                                    field.onChange(+e.target.value);
                                }}
                                label="Категория"
                            />
                            {fieldState.error && (
                                <FormHelperText
                                    error
                                    sx={{
                                        alignSelf: 'flex-start'
                                    }}
                                >
                                    {fieldState.error.message}
                                </FormHelperText>
                            )}
                        </>
                    )}
                />

                <Controller
                    control={control}
                    name="organizationId"
                    render={({field, fieldState}) => (
                        <OrganizationPublicAutocomplete
                            schoolId={studentInfo.group?.school?.id ?? 0}
                            fullWidth
                            label="Пункт сдачи"
                            onChange={(v) => {
                                field.onChange(v?.organizationId);
                            }}
                            message={fieldState.error?.message}
                        />
                    )}
                />

                {organizationId && (
                    <>
                        <Controller
                            control={control}
                            name="date"
                            render={({field}) => {
                                return (
                                    <DateCalendar
                                        loading={isScheduleMonthLoading}
                                        shouldDisableDate={(day) => !availableDays.find((el) =>{
                                            const d1 = day.startOf('day');
                                            const d2 = el.startOf('day');
                                            return d1.isSame(d2, 'day');
                                        })}
                                        onMonthChange={(v: Dayjs) => {
                                            field.onChange(v.toDate());
                                        }}
                                        value={dayjs(field.value)}
                                        onChange={(v: Dayjs) => {
                                            field.onChange(v.toDate());
                                        }}
                                        minDate={minDate}
                                    />
                                );
                            }}

                        />

                        {isScheduleDataLoading && (
                            <CircularProgress/>
                        )}
                        {slots.length > 0 && !isScheduleDataLoading && (
                            <Controller
                                control={control}
                                name="time"
                                render={({field, fieldState}) => {
                                    return (
                                        <>
                                            <Grid
                                                container
                                                spacing={4}
                                                sx={{
                                                    mb: 4,
                                                    alignSelf: 'stretch'
                                                }}
                                            >
                                                {slots.map((slot, i) => {
                                                    return (
                                                        <Grid
                                                            item
                                                            key={i}
                                                            xs={6}
                                                            sm={4}
                                                            lg={3}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            <Button
                                                                key={i}
                                                                variant={field.value === slot.time ? 'contained' : 'text'}
                                                                onClick={() => {
                                                                    field.onChange(slot.time);
                                                                }}
                                                            >
                                                                {slot.local}
                                                            </Button>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                            {fieldState.error?.message && (
                                                <FormHelperText
                                                    error
                                                    sx={{
                                                        alignSelf: 'flex-start'
                                                    }}
                                                >
                                                    {fieldState.error?.message}
                                                </FormHelperText>
                                            )}
                                        </>
                                    );
                                }}

                            />
                        )}
                        {slots && !isScheduleDataLoading && !slots.length && (
                            <>
                            <Typography>
                                Нет записей на выбранный день
                            </Typography>
                            </>
                        )}
                    </>
                )}

                <Box
                    sx={{
                        flex: '0 0 100%',
                        width: '100%'
                    }}
                >
                    <Typography>
                        Загрузить фотографию ИИН
                    </Typography>
                    <FileInputForm
                        control={control}
                        name="file"
                        acceptFileTypes={{
                            'application/pdf': ['.pdf'],
                            'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.heic', '.heif']
                        }}
                    />
                </Box>

                <Alert
                    sx={{
                        mt: 2
                    }}
                    severity={'info'}
                >
                    После отправки заявки с вами свяжется автошкола для подтверждения заявки. Если не связались,
                    обратитесь в свою автошколу.
                </Alert>

                <Box
                    sx={{
                        alignSelf: 'flex-end',
                        mt: 4
                    }}
                >
                    <LoadingButton
                        loading={isPending}
                        variant="contained"
                        type="submit"
                    >
                        Отправить
                    </LoadingButton>
                </Box>
            </Stack>
        </Stack>
    );
}
